import type { WrittenPage } from '../../sections'
import { LINKS } from '../links'

/**
 * Yapay Zeka Olgunluk Endeksi (AIMI) — çözüm sayfası, Türkçesi.
 *
 * Bölüm sırası, görseller, maddeler ve sayılar İngilizcesiyle birebir aynı;
 * değişen yalnızca nasıl söylendiği. Ürün adları ve yerleşik terimler İngilizce
 * kalıyor — AIMI, DAIMI, Impact Platform, AI Wizards, benchmark, roadmap,
 * quarterly business review.
 *
 * Müşteri sözleri çevrilmiyor: alıntı, söylendiği dilde durur.
 *
 * Açık sorular (eksik dört logo, kapanıştaki form, blog karuseli) İngilizce
 * dosyada ve orada bir kez duruyor — aynı karar iki dilde iki not olarak
 * sayılmasın.
 */
const PLATFORM = 'https://impact.digitopia.ai'

export const aimiSolution: WrittenPage = {
  navLabel: 'Yapay Zeka Olgunluk Endeksi',
  title: 'Yapay Zeka Olgunluk Endeksi (AIMI)',
  description:
    'AIMI, yapay zekada nerede durduğunuzu benchmark ile ortaya koyar, en değerli hamleleri önceliklendirir ve bunları uygulanabilir bir roadmap’e çevirir; hepsi Impact Platform üzerinden.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Yapay Zeka Olgunluk Endeksi (AIMI)',
      headline: 'Kurumunuz yapay zekaya ne kadar hazır?',
      sub: 'Nerede durduğunuzu benchmark ile görmek ve doğru stratejiyi kurmak için bir yapay zeka olgunluk değerlendirmesiyle başlayın. Yapay zekanın kurumunuza kattığı değeri en üst seviyeye çıkarın.',
      // Kapanış formu artık bu sayfanın altında; buton okuru aşağıdaki forma
      // indiriyor (2026-08-30).
      ctas: [{ label: 'Bize Ulaşın', href: '#contact' }],
      photo: {
        src: '/photo/aimi-assessment-presentation.webp',
        alt: 'Bir danışman, toplantı odasındaki ekranda AIMI skorunu, boyut bazındaki skorları ve sektör benchmark’ını anlatıyor.',
      },
    },
    {
      kind: 'logos',
      heading: 'Başarılı şirketler Digitopia ile çalışıyor.',
      items: [
        { name: 'dhiraagu', src: '/logo/dhiraagu.webp' },
        { name: 'DHL', src: '/logo/dhl.webp' },
        { name: 'Domino’s', src: '/logo/dominos.webp' },
        { name: 'Enerjisa', src: '/logo/enerjisa.webp' },
        { name: 'Intel', src: '/logo/intelaicard.png', padded: true },
        { name: 'Microsoft', src: '/logo/microsoft.webp' },
        { name: 'PROVEN', src: '/logo/provencard.png', padded: true },
      ],
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Elinize geçen şey bu',
      sub: 'AIMI size netlik, yön ve hemen uygulamaya alabileceğiniz bir büyüme planı verir.',
      points: [
        'AIMI, yapay zeka olgunluğunuz için net bir başlangıç noktası verir.',
        'Yapay zeka yetkinliklerinizin bugün tam olarak nerede olduğunu görürsünüz.',
        'Benchmark, sektörünüzdeki konumunuzu gösterir.',
        'AI Wizards, ekiplerinize her adımda yol gösterir.',
        'Yapay zekayı güvenli ve etkili biçimde ölçeklemek için önceliklendirilmiş bir roadmap alırsınız.',
        'Impact Platform ilerlemeyi görünür ve ölçülebilir tutar.',
      ],
      outro: ['Bunu kurumunuza uygulamaya hazır mısınız?'],
      photo: {
        src: '/photo/boardroom-applause-dashboards.webp',
        alt: 'Bir yönetim ekibi, arkasındaki ekranda dashboard’lar dururken değerlendirme oturumunun sonunda alkışlıyor.',
      },
      // Film 2026-08-30'da geldi; üstteki fotoğraf poster ve fallback olarak kalıyor.
      video: {
        src: 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/video/d556d28a-8939-49b5-ba7b-3e9368ce07a8-AIMI-2_V4_web_crf23.mp4',
        title: 'AIMI’ye kısa bir bakış: yapay zeka olgunluk başlangıç noktası, sektör benchmark’ı ve Impact Platform içindeki önceliklendirilmiş roadmap.',
      },
      // Kapanış formu artık bu sayfanın altında; buton okuru aşağıdaki forma
      // indiriyor (2026-08-30).
      ctas: [{ label: 'Bize Ulaşın', href: '#contact' }],
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'AIMI neden önemli',
      sub: 'Yöneticiler yapay zekanın önemini biliyor. Eksik olan şu:',
      points: [
        'net bir başlangıç noktası',
        'ortak bir olgunluk başlangıç seviyesi',
        'yapay zeka girişimlerini önceliklendirmenin tutarlı bir yolu',
        'iş değerine bağlanmış bir roadmap',
        'ekipleri hizada tutan bir sistem',
      ],
      outro: [
        'AIMI bunların her birini çözüyor.',
        'Yapay zekada ilerlemek için pratik bir yol mu arıyorsunuz?',
      ],
      photo: {
        src: '/photo/boardroom-daimi-target-state-applause.webp',
        alt: 'Bir yönetim ekibi, mevcut ve hedef durumlarını gösteren ekranın önünde alkışlıyor.',
      },
      // Kapanış formu artık bu sayfanın altında; buton okuru aşağıdaki forma
      // indiriyor (2026-08-30).
      ctas: [{ label: 'Bize Ulaşın', href: '#contact' }],
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Nasıl çalışıyor',
      points: [
        'Adım 1. Ölç. Mevcut yapay zeka olgunluğunuzu moderatörlü workshop’lar ve benchmark ile gözden geçirin.',
        'Adım 2. Önceliklendir. Öneri motorumuzla en yüksek değerli yapay zeka hamlelerini belirleyin.',
        'Adım 3. Uygula. İlerlemeyi Impact Platform içinde takip edin, quarterly business review’larla hizada kalın.',
      ],
      photo: {
        src: '/photo/impact-platform-target-state.webp',
        alt: 'Impact Platform’un hedef durum ekranı: endeks skoru, boyut bazındaki skorlar ve sektör benchmark’ı yan yana.',
      },
      ctas: [{ label: 'Bize Ulaşın', href: '#contact' }],
    },
    {
      kind: 'list',
      heading: 'AIMI geleneksel danışmanlıktan nasıl farklı',
      sub: 'Geleneksel danışmanlık',
      items: [
        'Uzun takvimler',
        'Kalabalık ekipler',
        'Slayta dayalı raporlama',
        'Sınırlı hizalanma',
        'Süregelen görünürlüğün olmaması',
      ],
    },
    {
      kind: 'list',
      sub: 'AIMI',
      items: [
        'Tek bir uzman danışman',
        'Günlük işe en az müdahale',
        'Anında online raporlama',
        'Her adımda yol gösteren AI Wizards',
        'Gerekçesiyle birlikte net öncelikler',
        'Impact Platform’da gerçek zamanlı görünürlük',
      ],
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Yapay zekadaki ilerlemeyi görünür ve ölçülebilir yapın',
      body: [
        'AIMI, yapay zeka olgunluğunuzu anlamanıza ve doğru öncelikleri belirlemenize yardım eder. Impact Platform da bu önceliklerin kalıcı bir uygulamaya dönüşmesini sağlar.',
        'Platformun içinde liderler yapay zeka girişimlerini takip eder, ilerlemeyi roadmap’e karşı izler ve ekipleri ortak hedefler etrafında hizada tutar. Gerçek zamanlı dashboard’lar, AI Wizards ve yapılandırılmış review’lar değerlendirmeden uygulamaya geçmeyi kolaylaştırır; yapay zeka ölçeklenirken ivmeyi korur.',
        'AIMI ve Impact Platform birlikte, yapay zeka dönüşümünüzün tamamında netlik, yön ve süregelen görünürlük sağlar.',
      ],
      photo: {
        src: '/photo/impact-platform-daimi-dashboard.webp',
        alt: 'Impact Platform’un ana ekranı bir laptop üzerinde: mevcut skor, yıllık ilerleme, milestone’lar ve devam eden boyut çalışmaları.',
      },
      ctas: [{ label: 'Impact Platform’u keşfedin', href: PLATFORM }],
    },
    {
      kind: 'quotes',
      variant: 'fame',
      items: [
        {
          quote:
            'Working with Digitopia has been a transformative experience for PROVEN. The Digital Maturity Index provided the clarity and structure we needed to assess our capabilities, while the interactive sessions delivered valuable, actionable insights. Benchmarking against industry peers gave us clear direction, ensuring smarter investments and stronger alignment across leadership. This initiative has set a solid foundation for our digital transformation, and we’re excited to build on this momentum.',
          source: 'Johann Heaven, CEO, PROVEN',
          company: 'PROVEN',
          logo: { name: 'PROVEN', src: '/logo/provencard.png' },
          portrait: { src: '/photo/portrait-johann-heaven.png', alt: 'Johann Heaven' },
        },
        {
          quote:
            'Digitopia’s Digital Maturity Index gave us the clarity about the positive progress we have made recently in creating business value with digitalization. We also had a chance to benchmark ourselves with the industry peers. Digitopia’s insightful recommendations helped us fine tune and confirm our strategy. This partnership has been a key milestone in our journey to innovation and digitalization.',
          source: 'Tekin Gülşen, VP of IT Y-EMEA and Global Applications',
          company: 'Yazaki',
          logo: { name: 'Yazaki', src: '/logo/yazakicard.png' },
          portrait: { src: '/photo/portrait-tekin-gulsen.png', alt: 'Tekin Gülşen' },
        },
        {
          quote:
            'Digitopia’s DMI assessments have helped us create clear, actionable digital roadmaps while staying on track. Their insights have been invaluable, guiding us to focus on critical areas like AI long before it became a major topic. As part of Sabancı Holding, it’s a significant advantage that other group companies also use Digitopia’s assessments, enabling a comprehensive view of digital maturity across the group. Digitopia is not just a partner; they play an essential role in shaping and driving our digital strategy.',
          source: 'Fatih Akar, Director of Global Information Technologies',
          company: 'Kordsa',
          logo: { name: 'Kordsa', src: '/logo/kordsacard.png' },
          portrait: { src: '/photo/portrait-fatih-akar.png', alt: 'Fatih Akar' },
        },
      ],
      cta: { label: 'Hikâyelerin tamamını okuyun', href: LINKS.hallOfFame },
    },
    // Kapanıştaki siyah CTA bandı kaldırıldı (2026-08-30) — sayfanın altındaki
    // form zaten orada.
    {
      kind: 'prose',
      align: 'center',
      heading: 'Yapay zeka olgunluğu üzerine okumalar',
      sub: 'Token ekonomisi, geciken kararlar, dört yapay zeka dönüşüm yolu. AIMI’nin arkasındaki düşünce, yazı yazı.',
      cta: { label: 'Impact Labs’i okuyun', href: LINKS.reports },
    },
  ],
}
